import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faShareNodes } from '@fortawesome/pro-light-svg-icons/faShareNodes';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons/faWhatsapp';
import { TypedMemo } from '../../../common/types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { openExternal } from '../../../../utils/openExternal';
import { SharePubliclyPreviewCover } from './SharePubliclyPreviewCover';
import { useCreateShareToken } from './hooks/useCreateShareToken';
import styles from './SharePubliclyModal.module.scss';
import { trackEvent } from '../../../analytics/sml/analytics';

interface SharePubliclyModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'artist' | 'track' | 'playlist';
  id: string;
  date: string;
  rangeDays: number;
  shareTitle: string;
  /** Entity name shown in the preview card. Falls back to the share title. */
  name?: string;
  /** Image used as the preview cover. */
  coverUrl?: string;
  /** Secondary line under the name in the preview card. */
  subtitle?: string;
}

const SharePubliclyModalComponent: React.FC<SharePubliclyModalProps> = ({
  isOpen,
  onClose,
  type,
  id,
  date,
  rangeDays,
  shareTitle,
  name,
  coverUrl,
  subtitle,
}) => {
  const intl = useIntl();
  const { showSuccess, showError } = useShowToast();
  const { mutateAsync, reset, isPending } = useCreateShareToken();
  const [url, setUrl] = useState<string | null>(null);

  // Generate a fresh token every time the modal opens. We capture the user's
  // current date snapshot at click-time, so the URL must be regenerated each
  // open rather than cached across the component lifecycle.
  useEffect(() => {
    if (!isOpen) {
      setUrl(null);
      reset();
      return;
    }

    let cancelled = false;
    mutateAsync({ type, id, date, rangeDays })
      .then((result) => {
        if (!cancelled) {
          setUrl(result.url);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error(err);
          showError(
            intl.formatMessage({
              id: 'app.sml.sharePublicly.error',
              defaultMessage: 'Could not generate share link. Please try again.',
            })
          );
        }
      });

    return () => {
      cancelled = true;
    };
    // We intentionally do not include `mutateAsync`/`reset` in the deps array.
    // Those are stable references from react-query but adding them risks
    // accidental re-firing. The modal lifecycle is driven by `isOpen`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type, id, date, rangeDays]);

  const handleCopy = useCallback(() => {
    if (url === null) {
      return;
    }
    navigator.clipboard
      .writeText(url)
      .then(() => {
        trackEvent('link_copied', { kind: 'report' });
        showSuccess(
          intl.formatMessage({
            id: 'app.sml.sharePublicly.copied',
            defaultMessage: 'Public link copied!',
          }),
          ''
        );
      })
      .catch((err: unknown) => {
        console.error(err);
        showError(
          intl.formatMessage({
            id: 'app.sml.sharePublicly.error',
            defaultMessage: 'Could not generate share link. Please try again.',
          })
        );
      });
  }, [url, intl, showSuccess, showError]);

  const handleShareX = useCallback(() => {
    if (url === null) {
      return;
    }
    openExternal(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareTitle)}`
    ).catch(console.error);
  }, [url, shareTitle]);

  const handleShareFacebook = useCallback(() => {
    if (url === null) {
      return;
    }
    openExternal(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`).catch(console.error);
  }, [url]);

  const handleShareWhatsApp = useCallback(() => {
    if (url === null) {
      return;
    }
    openExternal(`https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${url}`)}`).catch(console.error);
  }, [url, shareTitle]);

  const handleShareEmail = useCallback(() => {
    if (url === null) {
      return;
    }
    const mailto = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(url)}`;
    openExternal(mailto).catch(console.error);
  }, [url, shareTitle]);

  const isReady = url !== null;
  const showSpinner = isPending || !isReady;

  const displayName = name ?? shareTitle;
  const fallbackSubtitle = (() => {
    switch (type) {
      case 'track':
        return intl.formatMessage({ id: 'app.sml.sharePublicly.type.track', defaultMessage: 'Track' });
      case 'playlist':
        return intl.formatMessage({ id: 'app.sml.sharePublicly.type.playlist', defaultMessage: 'Playlist' });
      case 'artist':
        return intl.formatMessage({ id: 'app.sml.sharePublicly.type.artist', defaultMessage: 'Artist' });
      default:
        return intl.formatMessage({ id: 'app.sml.sharePublicly.type.artist', defaultMessage: 'Artist' });
    }
  })();
  const displaySubtitle = subtitle ?? fallbackSubtitle;
  const displayUrl = url ?? '';
  const displayUrlShort = displayUrl.replace(/^https?:\/\//, '');

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered contentClassName={styles.modalContent}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.titleIconWrap}>
            <FontAwesomeIcon icon={faShareNodes} className={styles.titleIcon} />
          </span>
          <h2 className={styles.title}>
            {intl.formatMessage({
              id: 'app.sml.sharePublicly.modal.title',
              defaultMessage: 'Share publicly',
            })}
          </h2>
        </div>
        <button type="button" onClick={onClose} className={styles.close} aria-label="Close">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <p className={styles.description}>
        {intl.formatMessage({
          id: 'app.sml.sharePublicly.description',
          defaultMessage:
            'Only public data is shared — your account, followed list and other private info stay hidden.',
        })}
      </p>

      <div className={styles.previewCard}>
        <SharePubliclyPreviewCover coverUrl={coverUrl} name={displayName} />
        <div className={styles.previewMeta}>
          <div className={styles.previewName}>{displayName}</div>
          <div className={styles.previewSubtitle}>{displaySubtitle}</div>
        </div>
      </div>

      {showSpinner ? (
        <div className={styles.spinnerWrapper}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className={styles.urlRow}>
            <FontAwesomeIcon icon={faGlobe} className={styles.urlIcon} />
            <input
              type="text"
              readOnly
              value={displayUrlShort}
              className={styles.urlInput}
              onFocus={(event) => event.target.select()}
              aria-label={intl.formatMessage({
                id: 'app.sml.sharePublicly.urlLabel',
                defaultMessage: 'Public share URL',
              })}
            />
            <button type="button" onClick={handleCopy} className={styles.copyButton}>
              <FontAwesomeIcon icon={faCopy} />
              {intl.formatMessage({ id: 'app.sml.sharePublicly.copy', defaultMessage: 'Copy' })}
            </button>
          </div>

          <div className={styles.shareToDivider}>
            <span>{intl.formatMessage({ id: 'app.sml.sharePublicly.shareTo', defaultMessage: 'Share to' })}</span>
          </div>

          <div className={styles.shareGrid}>
            <button type="button" className={styles.shareCard} onClick={handleShareX}>
              <span className={`${styles.shareIcon} ${styles.shareIconX}`}>
                <FontAwesomeIcon icon={faXTwitter} />
              </span>
              <span className={styles.shareLabel}>
                {intl.formatMessage({ id: 'app.sml.sharePublicly.x', defaultMessage: 'X' })}
              </span>
            </button>
            <button type="button" className={styles.shareCard} onClick={handleShareFacebook}>
              <span className={`${styles.shareIcon} ${styles.shareIconFacebook}`}>
                <FontAwesomeIcon icon={faFacebook} />
              </span>
              <span className={styles.shareLabel}>
                {intl.formatMessage({ id: 'app.sml.sharePublicly.facebook', defaultMessage: 'Facebook' })}
              </span>
            </button>
            <button type="button" className={styles.shareCard} onClick={handleShareWhatsApp}>
              <span className={`${styles.shareIcon} ${styles.shareIconWhatsApp}`}>
                <FontAwesomeIcon icon={faWhatsapp} />
              </span>
              <span className={styles.shareLabel}>
                {intl.formatMessage({ id: 'app.sml.sharePublicly.whatsapp', defaultMessage: 'WhatsApp' })}
              </span>
            </button>
            <button type="button" className={styles.shareCard} onClick={handleShareEmail}>
              <span className={`${styles.shareIcon} ${styles.shareIconEmail}`}>
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <span className={styles.shareLabel}>
                {intl.formatMessage({ id: 'app.sml.sharePublicly.email', defaultMessage: 'Email' })}
              </span>
            </button>
          </div>

          <div className={styles.footer}>
            <span className={styles.lockChip}>
              <FontAwesomeIcon icon={faLock} />
            </span>
            <div className={styles.footerText}>
              <div className={styles.footerTitle}>
                {intl.formatMessage({
                  id: 'app.sml.sharePublicly.footer.title',
                  defaultMessage: 'Anyone with the link can view',
                })}
              </div>
              <div className={styles.footerSubtitle}>
                {intl.formatMessage({
                  id: 'app.sml.sharePublicly.footer.subtitle',
                  defaultMessage: 'Read-only · no account info is shared',
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export const SharePubliclyModal = TypedMemo(SharePubliclyModalComponent);
